import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import MediaManagerModal from 'src/common/components/modals/MediaManagerModal';
import { UploadedFile } from 'src/interfaces/FileManager';
import { IBaseSponsor } from 'src/interfaces/Sponsor';

interface SponsorsEditorDialogProps {
  open: boolean;
  onClose: () => void;
  editingId?: string;
  form: IBaseSponsor;
  onFormChange: (form: IBaseSponsor) => void;
  onSave: () => void | Promise<void>;
  onImageSelect: (file: UploadedFile) => void;
  onRemoveImage: () => void;
}

export default function SponsorsEditorDialog({
  open,
  onClose,
  editingId,
  form,
  onFormChange,
  onSave,
  onImageSelect,
  onRemoveImage,
}: SponsorsEditorDialogProps) {
  const { t } = useTranslation();
  const [isMediaManagerOpen, setIsMediaManagerOpen] = useState(false);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? t('base.edit') : t('system.sponsors.add')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label={t('system.sponsors.name')}
            value={form.name}
            onChange={e => onFormChange({ ...form, name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('system.sponsors.url')}
            value={form.url}
            onChange={e => onFormChange({ ...form, url: e.target.value })}
          />
          <Box sx={{ mt: 2, mb: 1 }}>
            {form.imageUrl ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar
                  src={form.imageUrl}
                  variant="rounded"
                  sx={{ width: 80, height: 80, bgcolor: 'grey.200' }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ImageOutlinedIcon />}
                    onClick={() => setIsMediaManagerOpen(true)}
                  >
                    {t('system.sponsors.replaceLogo')}
                  </Button>
                  <Button size="small" color="error" onClick={onRemoveImage}>
                    {t('base.remove')}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Button
                variant="outlined"
                startIcon={<ImageOutlinedIcon />}
                onClick={() => setIsMediaManagerOpen(true)}
              >
                {t('system.sponsors.selectLogo')}
              </Button>
            )}
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={form.published ?? true}
                onChange={e => onFormChange({ ...form, published: e.target.checked })}
              />
            }
            label={t('system.sponsors.published')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.isOrganizer ?? false}
                onChange={e => onFormChange({ ...form, isOrganizer: e.target.checked })}
              />
            }
            label={t('system.sponsors.organizer')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('base.cancel')}</Button>
          <Button variant="contained" onClick={onSave}>
            {t('base.save')}
          </Button>
        </DialogActions>
      </Dialog>
      <MediaManagerModal
        open={isMediaManagerOpen}
        onClose={() => setIsMediaManagerOpen(false)}
        onSelect={onImageSelect}
        mode="select"
        endpoint="image"
        title={t('system.sponsors.selectLogo')}
        showUploadButton={true}
      />
    </>
  );
}
